/**
 * The Options block — one layout for every level (`docs/builder-engine-and-consistency.md` §4.3).
 *
 * Always the last section of a popover. Row 1 is **Optional** with its
 * sub-text, the one word the builder now shares with the customer's page; row
 * 2, only where the level has them, is a wrapped row of the flags a line or a
 * service carries. A flag is drawn exactly when its value is handed in, so a
 * level that does not have one simply does not pass it — there is no list of
 * levels here to drift from the data.
 *
 * Props-only: nothing in here reads a page or a hook, so the quote builder,
 * the package builder and the catalog pop-out all draw it the same way.
 */
import { CheckRow } from '../../components/form/rows.js'
import {
  CONSUMES_STOCK_LABEL, OPTIONAL_LABEL, OPTIONAL_SUB, OPTIONS_SECTION, SHOW_ON_PROPOSAL_LABEL,
  TAXABLE_LABEL, TAXABLE_SERVICE_SUB,
} from '../copy.js'

export interface OptionFlag {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}

export function OptionsBlock({
  level, optional, showOnProposal, taxable, consumesStock,
}: {
  level: 'visit' | 'service' | 'line'
  /** Absent where the level cannot be optional — a catalog service. */
  optional?: OptionFlag & { disabledReason?: string }
  showOnProposal?: OptionFlag
  taxable?: OptionFlag
  consumesStock?: OptionFlag
}) {
  const second = [
    showOnProposal && { key: 'proposal', label: SHOW_ON_PROPOSAL_LABEL, flag: showOnProposal },
    taxable && {
      key: 'taxable',
      label: TAXABLE_LABEL,
      // The service is the one level whose box reaches past itself.
      sub: level === 'service' ? TAXABLE_SERVICE_SUB : undefined,
      flag: taxable,
    },
    consumesStock && { key: 'stock', label: CONSUMES_STOCK_LABEL, flag: consumesStock },
  ].filter(Boolean) as { key: string; label: string; sub?: string; flag: OptionFlag }[]

  return (
    <section data-options-block={level} className="flex flex-col gap-1 border-t border-line-soft pt-4">
      <b className="text-[14px] font-bold text-text">{OPTIONS_SECTION}</b>
      {optional && (
        <CheckRow
          label={OPTIONAL_LABEL}
          sub={optional.disabled && optional.disabledReason
            ? optional.disabledReason
            : OPTIONAL_SUB}
          checked={optional.checked}
          disabled={optional.disabled}
          onChange={optional.onChange}
        />
      )}
      {second.length > 0 && (
        <div className="flex flex-wrap gap-x-[18px]">
          {second.map((one) => (
            <CheckRow
              key={one.key}
              label={one.label}
              sub={one.sub}
              checked={one.flag.checked}
              disabled={one.flag.disabled}
              onChange={one.flag.onChange}
            />
          ))}
        </div>
      )}
    </section>
  )
}
